import { WebDashboardContentSpecBlocksInner } from "@coeff/agent-types";
import { AnalyticsEventNameType } from "@coeff/api";
import React, { useCallback, useRef } from "react";

import { useTrack } from "../../utils";

import { BlockEditUpdates } from "./BlockEditPopover";
import { DashboardWindow } from "./dashboardHelpers";
import { RichTextEditor } from "./RichTextEditor";
import { useDismissOnOutside } from "./useDismissOnOutside";
import { usePopoverPosition } from "./usePopoverPosition";
import { useWebDashboardViewerContext } from "./WebDashboardViewerContext";

interface Props {
  block: WebDashboardContentSpecBlocksInner & { type: "text_block" };
  onSave: (blockId: string, updates: BlockEditUpdates) => void;
  onClose: () => void;
  targetWindow: DashboardWindow;
}

const POPOVER_WIDTH = 480;

export const TextBlockEditPopover: React.FC<Props> = ({ block, onSave, onClose, targetWindow }) => {
  const track = useTrack();
  const { dashboardId, dashboardName, spreadsheetId } = useWebDashboardViewerContext();
  const popoverRef = useRef<HTMLDivElement>(null);
  const { style: positionStyle } = usePopoverPosition(
    popoverRef,
    true,
    targetWindow,
    POPOVER_WIDTH
  );
  const getMarkdownRef = useRef<(() => string) | null>(null);

  useDismissOnOutside(popoverRef, onClose, targetWindow);

  const handleDone = useCallback(() => {
    const markdown = getMarkdownRef.current?.() ?? block.markdown;
    track(AnalyticsEventNameType.VibeDashBlockEdited, {
      vibe_dash_id: dashboardId,
      ss_id: spreadsheetId,
      vibe_dash_name: dashboardName,
      vibe_dash_block_id: block.block_id,
      vibe_dash_block_type: block.type,
      vibe_dash_edit_method: "manual",
    });
    onSave(block.block_id, { markdown });
    onClose();
  }, [
    block.block_id,
    block.markdown,
    block.type,
    onSave,
    onClose,
    track,
    dashboardId,
    spreadsheetId,
    dashboardName,
  ]);

  return (
    <div
      ref={popoverRef}
      className="absolute z-50 bg-white rounded-xl shadow-2xl border border-gray-200 max-h-[80vh] overflow-auto flex flex-col block-fade-in"
      style={positionStyle}
      onClick={e => e.stopPropagation()}
      onMouseDown={e => e.stopPropagation()}
    >
      <RichTextEditor
        initialMarkdown={block.markdown ?? ""}
        getMarkdownRef={getMarkdownRef}
        placeholder="Start typing..."
        minHeight="240px"
        showToolbar
        className="flex-1"
        namespace="TextBlockEditPopover"
      />

      <div className="px-4 py-2.5 border-t border-gray-100 flex items-center justify-end gap-2 shrink-0">
        <button
          onClick={onClose}
          className="px-4 py-1.5 text-gray-600 text-sm font-medium rounded-md hover:bg-gray-100 cursor-pointer transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleDone}
          className="px-4 py-1.5 bg-primary-button-background text-primary-button-foreground text-sm font-medium rounded-md hover:bg-primary-button-hover-background hover:text-primary-button-hover-foreground cursor-pointer transition-colors"
        >
          Done
        </button>
      </div>
    </div>
  );
};
